import { useEffect, useRef, useState } from 'react';
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, signInWithPopup, signOut, GoogleAuthProvider } from 'firebase/auth';
import { SignOut } from '../../design-system/icons';
import { useAuth, useProfile, firebaseConfig, ApiError } from '@discovr/common';
import { auth as authApi } from '@discovr/api';
import { Button } from '../ui/button';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { DisplayCurrencyField } from './DisplayCurrencyField';
// import { EditProfileDialog } from './EditProfileDialog'; // no BE endpoint yet, see below

/** Google's OAuth consent screen must render in the browser — there's no way
 *  around that. But this app never lets that browser-side step mutate the
 *  user's actual signed-in session: it runs on a throwaway, uniquely-named
 *  secondary Firebase App instance, purely to obtain a Google id_token, then
 *  tears the whole instance down immediately. The backend (POST
 *  /auth/link-google) does all the real work — verifying the token and
 *  attaching the provider via the Admin SDK — mirroring how password reset
 *  is entirely backend-driven. Never call linkWithPopup/linkWithCredential
 *  on the primary `auth.currentUser` here. */
async function getGoogleIdTokenFromThrowawayApp(): Promise<string> {
  const tempApp = initializeApp(firebaseConfig, `google-link-${Date.now()}`);
  try {
    const tempAuth = getAuth(tempApp);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const result = await signInWithPopup(tempAuth, provider);
    const idToken = await result.user.getIdToken();
    await signOut(tempAuth).catch(() => {});
    return idToken;
  } finally {
    await deleteApp(tempApp).catch(() => {});
  }
}

export interface AccountSectionProps {
  /** App-specific role label: "Owner"/"Member" for brand; "Admin"/"Staff" for admin. */
  roleLabel: string;
  /** Called when sign-out is clicked. Parent owns navigation after sign-out. */
  onSignOut: () => void;
}

/** Retries `user.reload()` to absorb the gap between a backend Admin-SDK
 *  link/unlink write and it becoming visible to a client-SDK read-back — a
 *  single attempt was observed to sometimes lose that race, leaving the UI
 *  showing stale state for an account the backend had already changed.
 *  Resolves `true` once a reload succeeds, `false` if every attempt fails. */
async function reloadWithRetries(user: { reload: () => Promise<void> }, attempts = 3): Promise<boolean> {
  for (let i = 0; i < attempts; i++) {
    try {
      await user.reload();
      return true;
    } catch {
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, 300 * (i + 1)));
    }
  }
  return false;
}

export function AccountSection({ roleLabel, onSignOut }: AccountSectionProps) {
  const { user } = useAuth();
  const { data: profile, refetch: refetchProfile } = useProfile();
  const [pwOpen, setPwOpen] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);
  // `onAuthStateChanged` doesn't fire for link/unlink operations (Firebase's uid-based
  // dedup means the auth-state listener never re-notifies). providerData on the `user`
  // object only picks up the change once `reloadWithRetries` re-fetches it from the
  // client SDK; this tick just forces a re-render once that happens, so the Google
  // avatar/display-name below (read fresh from `user` on every render) can update. It
  // is cosmetic only — see `googleLinked` below for why it never gates correctness.
  const [, setRefreshTick] = useState(0);
  // reloadWithRetries can take up to ~900ms (3 attempts, backing off) — long
  // enough for the user to navigate away or sign out mid-retry. Guards the
  // state updates that follow it so a slow retry never writes to an unmounted
  // component.
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Refetch on every mount (bypassing useProfile's 10-minute staleTime for this
  // one consumer) so Settings always shows the backend's current link state —
  // a page that was left open, or a browser tab restored from persistence,
  // must never show link status that's minutes (or a full session) stale.
  useEffect(() => {
    void refetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const providerData = user?.providerData ?? [];
  // Backend-authoritative: google_linked_at is the exact field Login's own
  // gate (decideGoogleLinkAction) trusts, so this can never disagree with
  // whether a real Google sign-in will actually succeed. Deliberately NOT
  // derived from the Firebase client SDK's `user.providerData` — that's a
  // locally cached snapshot (persisted to localStorage) that's only ever
  // refreshed by this component's own link/unlink handlers, so a stale tab,
  // a fresh page load, or a reload() that silently didn't complete could
  // show "Linked" (or "Not linked") long after the backend disagrees.
  const googleLinked = Boolean(profile?.google_linked_at);
  const hasPassword = providerData.some((p) => p.providerId === 'password');
  // Cosmetic only (avatar photo/display name) — falls back to the plain
  // "Linked" copy below when the client cache hasn't caught up yet.
  const googleProviderInfo = googleLinked ? providerData.find((p) => p.providerId === 'google.com') : undefined;

  const handleLinkGoogle = async () => {
    if (!user) return;
    setGoogleBusy(true);
    setGoogleError(null);
    try {
      // Obtains a Google id_token from a throwaway Firebase App instance —
      // never touches the primary signed-in session. The backend does all
      // the real work: verifying the token's email against this account and
      // attaching the provider via the Admin SDK. See
      // getGoogleIdTokenFromThrowawayApp and packages/api's auth.linkGoogle.
      const idToken = await getGoogleIdTokenFromThrowawayApp();
      await authApi.linkGoogle(idToken);
      if (!isMountedRef.current) return;
      // Refetch the profile — the backend already performed the actual
      // attach, and google_linked_at is what `googleLinked` reads, so this
      // is what actually flips the UI to "Linked". refetch() never throws
      // (TanStack Query resolves an error state rather than rejecting), so
      // a failure here must be checked explicitly — otherwise it silently
      // leaves the display on its pre-link (stale) state, which is exactly
      // the class of bug google_linked_at was introduced to prevent.
      const profileResult = await refetchProfile();
      if (!isMountedRef.current) return;
      if (profileResult.isError) {
        setGoogleError('Google was linked, but we could not confirm it here — reload the page to see the current status.');
        return;
      }
      // Best-effort cosmetic sync: refreshes the client SDK's cached
      // providerData so the Google avatar/display name can render. Never
      // gates correctness — see `googleLinked` above.
      await reloadWithRetries(user);
      if (!isMountedRef.current) return;
      setRefreshTick((t) => t + 1);
    } catch (err) {
      if (!isMountedRef.current) return;
      if (err instanceof ApiError) {
        setGoogleError(
          err.code === 'LINK_EMAIL_MISMATCH'
            ? 'That Google account has a different email address.'
            : err.code === 'LINK_ALREADY_LINKED_ELSEWHERE'
              ? 'That Google account is already linked to a different Discovr account.'
              : err.message || 'Could not link Google account. Please try again.',
        );
      } else {
        const code = (err as { code?: string } | null)?.code ?? '';
        if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
          setGoogleError('Could not link Google account. Please try again.');
        }
      }
    } finally {
      if (isMountedRef.current) setGoogleBusy(false);
    }
  };

  const handleUnlinkGoogle = async () => {
    if (!user) return;
    setGoogleBusy(true);
    setGoogleError(null);
    try {
      // Backend clears its own link record, removes the provider via the
      // Admin SDK, and revokes sessions — this app never calls Firebase's
      // client-side unlink() directly.
      await authApi.unlinkGoogle();
      if (!isMountedRef.current) return;
      // Refetch the profile — the backend already performed the actual
      // removal, and google_linked_at is what `googleLinked` reads, so this
      // is what actually flips the UI to "Not linked". refetch() never
      // throws (TanStack Query resolves an error state rather than
      // rejecting), so a failure here must be checked explicitly —
      // otherwise it silently leaves the display on its pre-unlink (stale,
      // "Linked") state, which is exactly the class of bug google_linked_at
      // was introduced to prevent.
      const profileResult = await refetchProfile();
      if (!isMountedRef.current) return;
      if (profileResult.isError) {
        setGoogleError('Google was unlinked, but we could not confirm it here — reload the page to see the current status.');
        return;
      }
      // Best-effort cosmetic sync: refreshes the client SDK's cached
      // providerData. Never gates correctness — see `googleLinked` above.
      await reloadWithRetries(user);
      if (!isMountedRef.current) return;
      setRefreshTick((t) => t + 1);
    } catch (err) {
      if (!isMountedRef.current) return;
      if (err instanceof ApiError && err.code === 'UNLINK_ONLY_PROVIDER') {
        setGoogleError('Set a password first — an account needs at least one sign-in method.');
      } else {
        setGoogleError('Could not unlink Google account. Please try again.');
      }
    } finally {
      if (isMountedRef.current) setGoogleBusy(false);
    }
  };

  const firstName = profile?.first_name ?? '';
  const lastName = profile?.last_name ?? '';

  const displayName = `${firstName} ${lastName}`.trim() || 'Your account';
  const email = user?.email ?? profile?.email ?? '';
  const initials = (firstName[0] ?? email[0] ?? '').toUpperCase();

  return (
    <div className="flex flex-col gap-4">
      {/* ── Account card — bordered per Figma (node 784:22411) ── */}
      <div className="rounded-xl border border-border bg-card p-5">
        {/* Header row: title + Logout button (Figma, top-right). */}
        <div className="mb-3 flex items-center justify-between gap-4">
          <h3 className="text-heading-h7 text-foreground">Account</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={onSignOut}
            className="gap-1.5 border-border-destructive-default text-foreground-destructive-default hover:bg-background-destructive-light-hover hover:text-foreground-destructive-default"
          >
            Logout <SignOut size={15} />
          </Button>
        </div>

        {/* Profile row */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-linear-to-br from-[#6c09f7] to-[#dd2a7b] text-body-md-semibold text-white">
              {initials}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-body-md-semibold text-foreground">{displayName}</span>
                <span className="rounded-full bg-(--stone-200) px-2 py-0.5 text-caption-medium tracking-[-0.013px] text-muted-foreground">{roleLabel}</span>
              </div>
              <span className="text-body-sm-regular text-muted-foreground">{email}</span>
            </div>
          </div>
          {/* Edit profile button — no BE endpoint yet (PUT /profile is GET-only
              for now, confirmed with Bornok), see EditProfileDialog usage below. */}
        </div>

        <div className="my-5 h-px bg-border" />

        <DisplayCurrencyField />

        <div className="my-5 h-px bg-border" />

        {/* Password */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="m-0 text-body-sm-medium text-foreground">Password</p>
            <p className="m-0 text-caption-regular text-muted-foreground">Set a new password for your account.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setPwOpen(true)}>Change password</Button>
        </div>

        <div className="my-5 h-px bg-border" />

        {/* Google account link */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-2.5">
            {googleLinked && googleProviderInfo?.photoURL && (
              <img
                src={googleProviderInfo.photoURL}
                alt=""
                referrerPolicy="no-referrer"
                className="size-8 shrink-0 rounded-full object-cover"
              />
            )}
            <div className="min-w-0">
              <p className="m-0 text-body-sm-medium text-foreground">Google account</p>
              <p className="m-0 truncate text-caption-regular text-muted-foreground">
                {googleLinked
                  ? googleProviderInfo?.displayName || googleProviderInfo?.email || 'Linked — you can sign in with Google.'
                  : 'Not linked'}
              </p>
            </div>
          </div>
          {googleLinked ? (
            <Button
              variant="outline" size="sm"
              onClick={() => void handleUnlinkGoogle()}
              disabled={googleBusy || !hasPassword}
              title={!hasPassword ? 'Set a password first — an account needs at least one sign-in method.' : undefined}
            >
              Unlink
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => void handleLinkGoogle()} disabled={googleBusy}>
              Link Google account
            </Button>
          )}
        </div>
        {googleError && <p role="alert" className="mt-2 text-caption-regular text-rose-600">{googleError}</p>}

        {/* Change theme — no dark mode implemented yet; coded but commented for later.
        <div className="my-5 h-px bg-border" />
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="m-0 text-body-sm-semibold text-foreground">Change Theme</p>
            <p className="m-0 text-caption-regular text-muted-foreground">Customize how the interface looks on your device.</p>
          </div>
          <div className="flex items-center gap-1 rounded-lg border border-border bg-muted p-1">
            <button className="flex items-center gap-1.5 rounded-md bg-card px-3 py-1.5 text-caption-medium text-foreground "><Monitor size={14}/> System</button>
            <button className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-caption-medium text-muted-foreground"><Sun size={14}/> Light</button>
            <button className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-caption-medium text-muted-foreground"><Moon size={14}/> Dark</button>
          </div>
        </div>
        */}

        <ChangePasswordDialog userEmail={email} open={pwOpen} onOpenChange={setPwOpen} />
        {/* Edit profile — no BE endpoint yet: PUT /profile is GET-only for now
        (confirmed with Bornok), so there's no working save path. Previously
        auto-opened + force-required whenever a user had no name saved, which
        trapped them (no way to close, no way to save). Re-enable once PUT
        /profile ships.
        <EditProfileDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          initialFirstName={firstName}
          initialLastName={lastName}
          required={!hasName}
        />
        */}
      </div>

      {/* Delete account — no BE endpoint yet; coded but commented for later.
      <div className="rounded-2xl border border-rose-200 bg-card p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="m-0 text-body-sm-semibold text-rose-600">Delete account</p>
            <p className="m-0 text-caption-regular text-muted-foreground">Permanently removes your account and data across every brand. This can't be undone.</p>
          </div>
          <Button className="bg-rose-600 text-white hover:bg-rose-700">Delete account</Button>
        </div>
      </div>
      */}

    </div>
  );
}
